import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, ref, onMounted } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { useResizeObserver } from '@vueuse/core';
import {
  SanityDocumentSchema,
  ISanityDocuments,
} from '~/types/sanity-documents';
import VideoPlayer, {
  IVideoPlayerWithMuxPlayer,
} from '~/components/Player/VideoPlayer.vue';
import VideoStats from '~/components/Player/VideoStats.vue';
import Chat from '~/components/Chat/Chat.vue';
import SanityBlockContent from './SanityBlockContent.vue';
import { useSanityDocument } from '~/composables/useSanityDocument';
import { useSanityDocumentChat } from '~/composables/useSanityDocumentChat';
import { useId } from '~/composables/useUniqueId';
import { useSanityDocumentList } from '~/composables/useSanityDocumentList';


export default /*#__PURE__*/_defineComponent({
  __name: 'WatchVideoPage',
  props: {
    doc: null,
    loading: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  doc?: ISanityDocuments[SanityDocumentSchema.VIDEO];
  loading: boolean;
};

const schema = SanityDocumentSchema.VIDEO;



const isChatExpanded = ref(true);
const muxPlayer = ref<IVideoPlayerWithMuxPlayer['muxPlayer']>(undefined);
const videoHeader = ref<HTMLDivElement | null>(null);
const videoPlayer = ref<IVideoPlayerWithMuxPlayer | undefined>(undefined);
const videoWrapper = ref<HTMLDivElement | null>(null);
const video = ref<HTMLVideoElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
const canvasCtx = ref<CanvasRenderingContext2D | null>(null);
const chatId = useId('chat');
const toggleChatId = useId('toggle-chat');

const headerHeight = ref<number>(0);
const videoHeight = ref<number>(0);
const videoTotalCountDisplayThreshold = +(
  process.env.videoTotalCountDisplayThreshold || 0
);

useResizeObserver(videoHeader, (entries) => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  headerHeight.value = height + 24;
});

useResizeObserver(videoWrapper, (entries) => {
  const entry = entries[0];
  const { height } = entry.contentRect;
  videoHeight.value = height;
});

const chatHeight = computed(
  () => (videoHeight.value ?? 0) + (headerHeight.value ?? 0),
);

const { doc: parentDoc, isLoading: isLoadingParent } = useSanityDocument<
  ISanityDocuments[SanityDocumentSchema.PROJECT]
>(
  computed(() => ({
    _id: props.doc?.documentReference?._ref,
    _type: SanityDocumentSchema.PROJECT,
  })),
);

const { doc: mediaTypeDoc, isLoading: isLoadingMediatype } = useSanityDocument<
  ISanityDocuments[SanityDocumentSchema.MEDIA_TYPE]
>(
  computed(() => ({
    _id: props.doc?.mediaType?._ref,
    _type: SanityDocumentSchema.MEDIA_TYPE,
  })),
);

const collectionIds = computed(() =>
  props.doc?.collections?.map((c) => c._ref),
);
const { docs: collections } = useSanityDocumentList<
  ISanityDocuments[SanityDocumentSchema.COLLECTION]
>(
  computed(() => ({
    _id: collectionIds.value,
    _type: SanityDocumentSchema.COLLECTION,
  })),
);

const { doc: muxAssetDoc, isLoading: isLoadingMuxAsset } = useSanityDocument<
  ISanityDocuments[SanityDocumentSchema.MUX_ASSET]
>(
  computed(() => ({
    _id: props.doc?.video?.asset?._ref,
    _type: SanityDocumentSchema.MUX_ASSET,
  })),
);

const isTransitionDone = ref(isChatExpanded.value);
const toggleTimeout = ref(0);
const toggleChat = () => {
  isChatExpanded.value = !isChatExpanded.value;
  if (toggleTimeout.value) {
    clearTimeout(toggleTimeout.value);
  }
  isTransitionDone.value = false;
  setTimeout(() => {
    isTransitionDone.value = true;
  }, 300);
};

const releaseDate = computed(() =>
  props.doc?.releaseDate ? new Date(props.doc?.releaseDate) : undefined,
);

const {
  channel,
  group: channelGroup,
  title: channelTitle,
  shouldShowChat,
  shouldEnableMessaging,
} = useSanityDocumentChat(
  computed(() => props.doc as ISanityDocuments[SanityDocumentSchema.VIDEO]),
  parentDoc,
);

const handlePlay = () => {
  (function loop() {
    if (
      canvas.value &&
      video.value &&
      !video.value?.paused &&
      !video.value?.ended
    ) {
      canvas.value.width = video.value.clientWidth;
      canvas.value.height = video.value.clientHeight;
      canvasCtx.value?.drawImage(
        video.value,
        0,
        0,
        video.value.clientWidth,
        video.value.clientHeight,
      );
      // @todo maybe pull max_stored_frame_rate from mux data
      setTimeout(loop, 1000 / 10); // drawing at 10fps
    }
  })();
};

const handlePlayerMounted = () => {
  if (videoPlayer?.value?.muxPlayer) {
    muxPlayer.value = videoPlayer.value.muxPlayer;
    const muxVideo = muxPlayer.value.shadowRoot?.querySelector('mux-video');
    video.value = muxVideo?.shadowRoot?.querySelector('video') ?? null;
  }
};

onMounted(() => {
  canvasCtx.value = canvas.value?.getContext('2d') ?? null;
});

return { __sfc: true,schema, props, isChatExpanded, muxPlayer, videoHeader, videoPlayer, videoWrapper, video, canvas, canvasCtx, chatId, toggleChatId, headerHeight, videoHeight, videoTotalCountDisplayThreshold, chatHeight, parentDoc, isLoadingParent, mediaTypeDoc, isLoadingMediatype, collectionIds, collections, muxAssetDoc, isLoadingMuxAsset, isTransitionDone, toggleTimeout, toggleChat, releaseDate, channel, channelGroup, channelTitle, shouldShowChat, shouldEnableMessaging, handlePlay, handlePlayerMounted, VideoPlayer, VideoStats, Chat, SanityBlockContent }
}

})